import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

interface InvitationData {
  id: string;
  email: string;
  organization_name: string;
  role: string;
  expires_at: string;
  license_id: string;
}

const shellClassName = 'auth-screen min-h-screen flex items-center justify-center p-4';
const cardClassName = 'auth-card w-full max-w-md rounded p-8 text-center';
const inputClassName = 'auth-input w-full rounded px-4 py-2';
const labelClassName = 'auth-label mb-1 block text-left';

export default function ActivationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');
  const [invitation, setInvitation] = useState<InvitationData | null>(null);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');

  useEffect(() => {
    if (!token) {
      setError('Token de ativacao nao encontrado na URL');
      setLoading(false);
      return;
    }

    void validateToken();
  }, [token]);

  async function validateToken() {
    try {
      setLoading(true);
      setError('');

      const { data, error: fetchError } = await supabase
        .from('user_invitations')
        .select('*')
        .eq('token', token)
        .eq('status', 'pending')
        .single();

      if (fetchError || !data) {
        setError('Token de ativacao invalido ou ja utilizado');
        setLoading(false);
        return;
      }

      const expiresAt = new Date(data.expires_at);
      if (expiresAt < new Date()) {
        setError('Este link de ativacao expirou. Solicite um novo convite.');
        setLoading(false);
        return;
      }

      setInvitation(data);
      setLoading(false);
    } catch (err: any) {
      setError('Erro ao validar token. Tente novamente.');
      setLoading(false);
    }
  }

  async function handleActivate(event: React.FormEvent) {
    event.preventDefault();

    if (!invitation) {
      setError('Dados do convite nao encontrados');
      return;
    }

    if (!fullName.trim()) {
      setError('Por favor, informe seu nome completo');
      return;
    }

    if (password.length < 8) {
      setError('A senha deve ter no minimo 8 caracteres');
      return;
    }

    if (password !== confirmPassword) {
      setError('As senhas nao coincidem');
      return;
    }

    try {
      setValidating(true);
      setError('');

      const { data: authData, error: signUpError } = await supabase.auth.signUp({
        email: invitation.email,
        password,
        options: {
          data: {
            full_name: fullName,
            organization_name: invitation.organization_name,
            role: invitation.role,
          },
        },
      });

      if (signUpError) {
        if (signUpError.message.includes('already registered')) {
          setError('Este email ja possui uma conta. Tente fazer login.');
          setTimeout(() => navigate('/'), 2000);
          return;
        }

        throw signUpError;
      }

      await supabase
        .from('user_invitations')
        .update({
          status: 'accepted',
          accepted_at: new Date().toISOString(),
        })
        .eq('id', invitation.id);

      try {
        await supabase
          .from('user_profiles')
          .insert({
            user_id: authData.user!.id,
            email: invitation.email,
            name: fullName,
            full_name: fullName,
          });
      } catch (profileError) {
        console.warn('Erro ao criar user_profiles:', profileError);
      }

      setTimeout(() => {
        navigate('/');
      }, 2000);

      setError('');
    } catch (err: any) {
      setError(err.message || 'Erro ao ativar conta. Tente novamente.');
      setValidating(false);
    }
  }

  if (loading) {
    return (
      <div className={shellClassName}>
        <div className={cardClassName}>
          <div
            className="mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-b-4"
            style={{ borderColor: 'var(--brand-primary)' }}
          />
          <p className="auth-muted text-lg">Validando seu convite...</p>
        </div>
      </div>
    );
  }

  if (error && !invitation) {
    return (
      <div className={shellClassName}>
        <div className={cardClassName}>
          <div className="mb-4 text-4xl enterprise-text-primary">Acesso indisponivel</div>
          <h2 className="mb-4 text-2xl font-bold enterprise-text-primary">Token invalido</h2>
          <p className="auth-muted mb-6">{error}</p>
          <button onClick={() => navigate('/')} className="auth-primary-button rounded px-6 py-3 transition">
            Voltar ao inicio
          </button>
        </div>
      </div>
    );
  }

  if (validating && !error) {
    return (
      <div className={shellClassName}>
        <div className={cardClassName}>
          <div className="mb-4 text-4xl enterprise-text-primary">Conta ativada</div>
          <h2 className="mb-4 text-2xl font-bold enterprise-text-primary">Tudo certo</h2>
          <p className="auth-muted mb-4">Sua conta foi criada com sucesso.</p>
          <p className="auth-faint text-sm">Redirecionando para o aplicativo...</p>
          <div
            className="mx-auto mt-4 h-8 w-8 animate-spin rounded-full border-b-2"
            style={{ borderColor: 'var(--brand-primary)' }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className={shellClassName}>
      <div className="auth-card w-full max-w-md rounded p-8">
        <div className="mb-8 text-center">
          <div className="mb-4 text-4xl enterprise-text-primary">Ativacao</div>
          <h1 className="mb-2 text-3xl font-bold enterprise-text-primary">Bem-vindo ao Workflow</h1>
          <p className="auth-muted">
            Ative sua conta para <strong>{invitation?.organization_name}</strong>
          </p>
        </div>

        <form onSubmit={handleActivate} className="space-y-4">
          <div>
            <label className={labelClassName}>Email</label>
            <input
              type="email"
              value={invitation?.email || ''}
              disabled
              className={inputClassName}
              style={{ cursor: 'not-allowed', opacity: 0.8 }}
            />
          </div>

          <div>
            <label className={labelClassName}>Nome completo</label>
            <input
              type="text"
              value={fullName}
              onChange={(event) => setFullName(event.target.value)}
              placeholder="Seu nome completo"
              required
              className={inputClassName}
            />
          </div>

          <div>
            <label className={labelClassName}>Senha</label>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Minimo 8 caracteres"
              required
              minLength={8}
              className={inputClassName}
            />
          </div>

          <div>
            <label className={labelClassName}>Confirmar senha</label>
            <input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Digite a senha novamente"
              required
              minLength={8}
              className={inputClassName}
            />
          </div>

          <div
            className="rounded border p-4 text-sm"
            style={{
              backgroundColor: 'color-mix(in srgb, var(--brand-primary) 10%, var(--bg-surface-1))',
              borderColor: 'color-mix(in srgb, var(--brand-primary) 35%, var(--border-strong))',
              color: 'var(--text-primary)',
            }}
          >
            <strong>Funcao:</strong>{' '}
            {invitation?.role === 'admin'
              ? 'Administrador'
              : invitation?.role === 'editor'
                ? 'Editor'
                : 'Visualizador'}
          </div>

          {error ? (
            <div className="auth-error-banner rounded p-4">
              <p className="text-sm">{error}</p>
            </div>
          ) : null}

          <button
            type="submit"
            disabled={validating}
            className="auth-primary-button w-full rounded py-3 font-semibold transition disabled:cursor-not-allowed disabled:opacity-50"
          >
            {validating ? 'Ativando conta...' : 'Ativar minha conta'}
          </button>
        </form>

        <p className="auth-faint mt-6 text-center text-xs">
          Este link expira em 7 dias. Caso tenha expirado, solicite um novo convite.
        </p>
      </div>
    </div>
  );
}
